import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { paymentsAPI } from '../lib/api';
import { CheckCircle, XCircle, Loader2, AlertTriangle } from 'lucide-react';

const POLL_INTERVAL_MS = 2500;
const MAX_ATTEMPTS = 24; // ~60 seconds

const formatCOP = (n) =>
  new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(Number(n) || 0);

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get('session_id');
  const attemptsRef = useRef(0);
  const timerRef = useRef(null);

  const [state, setState] = useState({
    status: 'polling', // polling | paid | failed | expired | timeout
    info: null,
    error: null,
  });

  useEffect(() => {
    if (!sessionId) {
      setState({ status: 'failed', error: 'Sesión de pago no encontrada', info: null });
      return;
    }

    const poll = async () => {
      attemptsRef.current += 1;
      try {
        const res = await paymentsAPI.getStatus(sessionId);
        const data = res.data;
        if (data.payment_status === 'paid') {
          setState({ status: 'paid', info: data, error: null });
          return;
        }
        if (data.status === 'expired') {
          setState({ status: 'expired', info: data, error: null });
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState({ status: 'timeout', info: data, error: null });
          return;
        }
        timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        const msg = err?.response?.data?.detail || 'Error al verificar el pago';
        setState({ status: 'failed', error: msg, info: null });
      }
    };
    poll();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [sessionId]);

  const renderContent = () => {
    if (state.status === 'polling') {
      return (
        <>
          <Loader2 className="h-16 w-16 text-accent animate-spin mx-auto mb-6" />
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight mb-2">Verificando tu pago...</h1>
          <p className="text-muted-foreground">Esto puede tardar unos segundos. No cierres esta ventana.</p>
        </>
      );
    }
    if (state.status === 'paid') {
      const count = state.info?.listing_ids?.length || 1;
      return (
        <>
          <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-6" />
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight mb-2">¡Pago exitoso!</h1>
          <p className="text-muted-foreground mb-2">
            {count === 1
              ? 'Tu anuncio se ha publicado correctamente.'
              : `Se han publicado ${count} anuncios correctamente.`}
          </p>
          {state.info?.amount_total ? (
            <p className="text-sm text-muted-foreground">Total cobrado: {formatCOP(state.info.amount_total)}</p>
          ) : null}
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
            <Button className="btn-accent" onClick={() => navigate('/dashboard')} data-testid="payment-go-dashboard">
              Ir al panel
            </Button>
            {count === 1 && state.info?.listing_ids?.[0] && (
              <Link to={`/listings/${state.info.listing_ids[0]}`}>
                <Button variant="outline">Ver mi anuncio</Button>
              </Link>
            )}
          </div>
        </>
      );
    }
    if (state.status === 'expired') {
      return (
        <>
          <XCircle className="h-16 w-16 text-red-600 mx-auto mb-6" />
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight mb-2">Sesión expirada</h1>
          <p className="text-muted-foreground mb-6">Tu sesión de pago caducó. Por favor, inténtalo de nuevo desde el panel.</p>
          <Button className="btn-accent" onClick={() => navigate('/dashboard')}>Ir al panel</Button>
        </>
      );
    }
    if (state.status === 'timeout') {
      return (
        <>
          <AlertTriangle className="h-16 w-16 text-yellow-600 mx-auto mb-6" />
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight mb-2">Confirmación pendiente</h1>
          <p className="text-muted-foreground mb-6">
            Aún no recibimos la confirmación de Stripe. Revisa tu panel en unos minutos; si el pago se aprobó,
            tu anuncio aparecerá publicado automáticamente.
          </p>
          <Button className="btn-accent" onClick={() => navigate('/dashboard')}>Ir al panel</Button>
        </>
      );
    }
    // failed
    return (
      <>
        <XCircle className="h-16 w-16 text-red-600 mx-auto mb-6" />
        <h1 className="font-heading text-3xl font-bold uppercase tracking-tight mb-2">Error al verificar el pago</h1>
        <p className="text-muted-foreground mb-6">{state.error}</p>
        <Button className="btn-accent" onClick={() => navigate('/dashboard')}>Volver al panel</Button>
      </>
    );
  };

  return (
    <div className="min-h-screen bg-secondary flex items-center justify-center px-4" data-testid="payment-success-page">
      <div className="max-w-lg w-full bg-white border border-border rounded-md p-10 text-center">
        {renderContent()}
      </div>
    </div>
  );
}
